<div class="tile-padding-first tile-padding admin-page">

  @if (available === false) {
    <p id="emailHistoryUnavailable">Email history is not switched on in this environment.</p>
  }

  @if (available) {
    <!-- ── Summary ────────────────────────────────────────────────────────────────────────────── -->
    <section class="admin-section" id="emailHistorySummary">
      <div class="admin-toolbar admin-toolbar--center">
        <h2 class="admin-h2 tw:mr-auto">Email history</h2>
        <div class="btn-group" role="group" aria-label="Summary window" id="emailHistoryWindow">
          @for (w of windows; track w.hours) {
            <button type="button" class="btn btn-sm" [attr.data-window]="w.hours"
              [class.btn-primary]="hours === w.hours" [class.btn-secondary]="hours !== w.hours"
              [attr.aria-pressed]="hours === w.hours" (click)="windowChanged(w.hours)">{{w.label}}</button>
          }
        </div>
        <label class="tw:flex tw:items-center tw:gap-1 tw:mb-0 tw:text-sm">
          <input type="checkbox" id="emailHistoryAutoRefresh" [ngModel]="autoRefresh" (ngModelChange)="autoRefreshChanged($event)" />
          Auto-refresh
        </label>
      </div>
      <p class="admin-lede">Every email the system tried to send, and whether each one went. This page only reads: it sends nothing.</p>

      @if (summaryError) {
        <div class="tw:my-2 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" id="emailHistorySummaryError" role="alert">
          The summary could not be loaded.
        </div>
      }
      @if (summaryLoading && !summary) {
        <div class="tw:flex tw:justify-center tw:my-4" data-spinner="summary"><mat-progress-spinner mode="indeterminate" [diameter]="32"></mat-progress-spinner></div>
      }

      @if (summary) {
        @if (summary.redirectedTo) {
          <div class="tw:my-2 tw:p-3 tw:border tw:border-blue-400 tw:bg-blue-50 tw:text-blue-900 tw:rounded" id="emailHistoryRedirected">
            This environment redirects all email to <b>{{summary.redirectedTo}}</b>.
          </div>
        }

        <div class="tw:grid tw:grid-cols-2 tw:md:grid-cols-5 tw:gap-3 tw:my-3" id="emailHistoryKpis">
          <div class="admin-panel" data-kpi="attempted">
            <div class="tw:text-sm tw:text-gray-600">Attempted</div>
            <div class="tw:text-2xl tw:font-semibold">{{attempted}}</div>
            <div class="tw:text-xs tw:text-gray-600">of {{summary.total}} logged</div>
          </div>
          <div class="admin-panel" data-kpi="sent">
            <div class="tw:text-sm tw:text-gray-600">Sent</div>
            <div class="tw:text-2xl tw:font-semibold">{{summary.sent}}</div>
          </div>
          <div class="admin-panel" data-kpi="success" [ngClass]="{'tw:border-red-500 tw:bg-red-50': successLow}"
            [attr.data-alert]="successLow ? 'true' : null">
            <div class="tw:text-sm tw:text-gray-600">Success</div>
            <div class="tw:text-2xl tw:font-semibold" [ngClass]="{'tw:text-red-700': successLow}">{{percent(summary.successRate)}}</div>
            @if (summary.successRate == null) { <div class="tw:text-xs tw:text-gray-600">nothing attempted</div> }
          </div>
          <div class="admin-panel" data-kpi="problems" [ngClass]="{'tw:border-red-500 tw:bg-red-50': problems > 0}"
            [attr.data-alert]="problems > 0 ? 'true' : null">
            <div class="tw:text-sm tw:text-gray-600">Not sent: problems</div>
            <div class="tw:text-2xl tw:font-semibold" [ngClass]="{'tw:text-red-700': problems > 0}">{{problems}}</div>
            <div class="tw:text-xs tw:text-gray-600">{{summary.failed}} failed · {{summary.refused}} refused · {{summary.wordingFailed}} wording</div>
          </div>
          <div class="admin-panel tw:text-gray-600" data-kpi="suppressed">
            <div class="tw:text-sm">Suppressed</div>
            <div class="tw:text-2xl tw:font-semibold">{{summary.suppressed}}</div>
            <div class="tw:text-xs">switched off on System Controls</div>
          </div>
        </div>

        <details class="tw:mb-3" id="emailHistoryLegend">
          <summary class="tw:text-sm"><b>What the outcomes mean</b></summary>
          <dl class="admin-dl tw:text-sm tw:mt-2">
            @for (o of outcomes; track o.value) {
              <dt><span class="tw:inline-block tw:px-2 tw:rounded tw:border tw:text-xs {{o.badge}}">{{o.label}}</span></dt>
              <dd>{{o.meaning}}</dd>
            }
          </dl>
        </details>

        <div class="tw:grid tw:grid-cols-1 tw:xl:grid-cols-3 tw:gap-6">
          <div class="tw:xl:col-span-2">
            <h3 class="admin-h3">By email</h3>
            @if (summary.byProcess.length) {
              <table class="table admin-table" id="emailHistoryByProcess">
                <thead>
                  <tr>
                    <th>Process</th><th>Sent</th><th>Suppressed</th><th>Refused</th><th>Wording failed</th><th>Failed</th>
                    <th>Last sent</th><th>Last failure</th>
                  </tr>
                </thead>
                <tbody>
                  @for (p of summary.byProcess; track p.processKey) {
                    <tr [attr.data-process]="p.processKey" [ngClass]="{'tw:cursor-pointer': !!p.processKey}" [class.table-active]="!!p.processKey && p.processKey === processKey"
                      [title]="p.processKey ? 'Show the history of ' + p.processKey : ''" (click)="filterByProcess(p)">
                      <td data-label="Process"><code>{{p.processKey || '(none)'}}</code></td>
                      <td data-label="Sent">{{p.sent}}</td>
                      <td data-label="Suppressed" class="tw:text-gray-600">{{p.suppressed}}</td>
                      <td data-label="Refused" [ngClass]="{'tw:text-red-700': p.refused > 0}">{{p.refused}}</td>
                      <td data-label="Wording failed" [ngClass]="{'tw:text-red-700': p.wordingFailed > 0}">{{p.wordingFailed}}</td>
                      <td data-label="Failed" [ngClass]="{'tw:text-red-700': p.failed > 0}">{{p.failed}}</td>
                      <td data-label="Last sent" [title]="p.lastSentUtc ? (p.lastSentUtc | date:'medium') : ''">{{ago(p.lastSentUtc) || '–'}}</td>
                      <td data-label="Last failure">
                        @if (p.lastFailureUtc) {
                          <span [title]="p.lastFailureUtc | date:'medium'">{{ago(p.lastFailureUtc)}}</span>
                          @if (p.lastFailureReason) { <div class="tw:text-xs tw:text-red-800 admin-wrap">{{p.lastFailureReason}}</div> }
                        } @else { – }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            } @else {
              <p class="tw:text-sm tw:text-gray-600" id="emailHistoryByProcessEmpty">No email was logged in this window.</p>
            }
          </div>

          <div>
            <h3 class="admin-h3">Recent problems</h3>
            @if (summary.recentFailures.length) {
              <ul class="tw:list-none tw:p-0 tw:m-0" id="emailHistoryRecentFailures">
                @for (f of summary.recentFailures; track f.id) {
                  <li class="tw:border-b tw:py-2 tw:text-sm" [attr.data-failure]="f.id">
                    <span class="tw:inline-block tw:px-2 tw:rounded tw:border tw:text-xs {{info(f.outcome).badge}}">{{info(f.outcome).label}}</span>
                    <code class="tw:ml-1">{{f.processKey || f.templateKey}}</code>
                    <span class="tw:text-gray-600 tw:ml-1" [title]="f.createdUtc | date:'medium'">{{ago(f.createdUtc)}}</span>
                    <div class="admin-wrap">{{f.subject}}</div>
                    @if (f.error) { <div class="tw:text-red-800 admin-wrap">{{f.error}}</div> }
                  </li>
                }
              </ul>
            } @else {
              <p class="tw:text-sm tw:text-gray-600" id="emailHistoryNoProblems">No problems in this window.</p>
            }
          </div>
        </div>
      }
    </section>

    <!-- ── History list ───────────────────────────────────────────────────────────────────────── -->
    <section class="admin-section" id="emailHistoryList">
      <h3 class="admin-h3">History</h3>

      <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-5 tw:gap-3 tw:mb-3" id="emailHistoryFilters">
        <div>
          <label class="field__label" for="emailHistoryOutcome">Outcome</label>
          <select id="emailHistoryOutcome" class="field__control" [(ngModel)]="outcome" (ngModelChange)="filtersChanged()">
            <option value="">All</option>
            @for (o of outcomes; track o.value) { <option [value]="o.value">{{o.label}}</option> }
          </select>
        </div>
        <div>
          <label class="field__label" for="emailHistoryProcess">Process</label>
          <select id="emailHistoryProcess" class="field__control" [(ngModel)]="processKey" (ngModelChange)="filtersChanged()">
            <option value="">All</option>
            @for (k of processKeys; track k) { <option [value]="k">{{k}}</option> }
          </select>
        </div>
        <div>
          <label class="field__label" for="emailHistorySearch">Recipient or subject</label>
          <input id="emailHistorySearch" class="field__control" type="search" maxlength="200" [(ngModel)]="search" (ngModelChange)="searchEdited()" />
        </div>
        <div>
          <label class="field__label" for="emailHistoryFrom">From</label>
          <input id="emailHistoryFrom" class="field__control" type="date" [(ngModel)]="fromDate" (ngModelChange)="filtersChanged()" />
        </div>
        <div>
          <label class="field__label" for="emailHistoryTo">To</label>
          <input id="emailHistoryTo" class="field__control" type="date" [(ngModel)]="toDate" (ngModelChange)="filtersChanged()" />
        </div>
      </div>

      <div class="admin-toolbar admin-toolbar--center tw:mb-2">
        <span class="tw:mr-auto tw:text-sm" id="emailHistoryCount">
          @if (result) {
            @if (result.total) { Showing {{firstShown}}–{{lastShown}} of {{result.total}} } @else { No emails match these filters. }
          }
        </span>
        <button type="button" class="btn btn-link btn-sm" (click)="clearFilters()">Clear filters</button>
        <button type="button" class="btn btn-secondary btn-sm" id="emailHistoryPrevious" [disabled]="!hasPrevious || listLoading" (click)="previousPage()">Previous</button>
        <button type="button" class="btn btn-secondary btn-sm" id="emailHistoryNext" [disabled]="!hasNext || listLoading" (click)="nextPage()">Next</button>
      </div>

      @if (listLoading) {
        <div class="tw:flex tw:justify-center tw:my-4" data-spinner="list" id="emailHistoryListLoading"><mat-progress-spinner mode="indeterminate" [diameter]="32"></mat-progress-spinner></div>
      } @else if (listError) {
        <div class="tw:my-2 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" id="emailHistoryListError" role="alert">
          The email history could not be loaded.
          <button type="button" class="btn btn-link btn-sm" (click)="loadList()">Try again</button>
        </div>
      } @else if (result && result.items.length) {
        <table class="table admin-table" id="emailHistoryTable">
          <thead>
            <tr><th>Time</th><th>Process</th><th>Subject</th><th>To</th><th>Outcome</th><th>Redirected to</th><th>Error</th></tr>
          </thead>
          <tbody>
            @for (e of result.items; track e.id) {
              <tr class="tw:cursor-pointer" [attr.data-entry]="e.id" [attr.aria-expanded]="expandedId === e.id" (click)="toggle(e)">
                <td data-label="Time" class="tw:whitespace-nowrap">{{e.createdUtc | date:'d MMM y, HH:mm:ss'}}</td>
                <td data-label="Process"><code>{{e.processKey}}</code></td>
                <td data-label="Subject" class="admin-wrap">{{e.subject}}</td>
                <td data-label="To" [title]="e.to.join(', ')"><admin-email [value]="recipients(e)" /></td>
                <td data-label="Outcome">
                  <span class="tw:inline-block tw:px-2 tw:rounded tw:border tw:text-xs {{info(e.outcome).badge}}" data-outcome-badge
                    [attr.data-outcome]="e.outcome" [attr.data-problem]="info(e.outcome).problem" [title]="info(e.outcome).meaning">{{info(e.outcome).label}}</span>
                </td>
                <td data-label="Redirected to">{{e.redirectedTo}}</td>
                <td data-label="Error" class="admin-wrap tw:text-red-800">{{e.error}}</td>
              </tr>
              @if (expandedId === e.id) {
                <tr [attr.data-entry-detail]="e.id">
                  <td colspan="7">
                    <dl class="admin-dl tw:text-sm">
                      <dt>Id</dt><dd>{{e.id}}</dd>
                      <dt>Time</dt><dd>{{e.createdUtc | date:'d MMM y, HH:mm:ss.SSS'}} ({{e.createdUtc}})</dd>
                      <dt>Process key</dt><dd><code>{{e.processKey}}</code></dd>
                      <dt>Template key</dt><dd><code>{{e.templateKey}}</code></dd>
                      <dt>Subject</dt><dd>{{e.subject}}</dd>
                      <dt>To</dt><dd>{{e.to.join(', ')}}</dd>
                      <dt>Bcc</dt><dd>{{e.bccCount}} {{e.bccCount === 1 ? 'address' : 'addresses'}}</dd>
                      <dt>Brand</dt><dd>{{e.brand}} @if (e.applicationId != null) { (application {{e.applicationId}}) }</dd>
                      <dt>Outcome</dt><dd>{{info(e.outcome).label}}: {{info(e.outcome).meaning}}</dd>
                      <dt>Redirected to</dt><dd>{{e.redirectedTo || '–'}}</dd>
                      <dt>SendGrid status</dt><dd>{{e.sendGridStatus ?? '–'}}</dd>
                      <dt>SendGrid message id</dt><dd><code data-message-id>{{e.sendGridMessageId || '–'}}</code></dd>
                      <dt>Error</dt><dd>{{e.error || '–'}}</dd>
                      <dt>Duration</dt><dd>{{e.durationMs != null ? e.durationMs + ' ms' : '–'}}</dd>
                    </dl>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      }
    </section>
  }
</div>
